import { BareActionContext } from "vuex-typex";
import { storeBuilder, RootState } from "../RootState";
import Messages, { Message } from "../../core/messages";

const LOCAL_STORAGE_KEY = "inbox-state";

export class InboxState {
  userId = "";
  messages: Message[] = [];
  language = "american";
  isLoading = false;
  settingsIsVisible = false;
  newMessageIsVisible = false;
  deviceId = "";
  fcmToken = "";
  roonPlatform = "Mac";
  roonVersion = "100700610";
  roonProduct = "roon";
}

const b = storeBuilder.module<InboxState>("inbox", new InboxState());

// getters
const userId = b.read((state) => state.userId, "userId");
const language = b.read((state) => state.language, "language");
const messages = b.read((state) => state.messages, "messages");
const deviceId = b.read((state) => state.deviceId, "deviceId");
const fcmToken = b.read((state) => state.fcmToken, "fcmToken");
const roonPlatform = b.read((state) => state.roonPlatform, "roonPlatform");
const roonVersion = b.read((state) => state.roonVersion, "roonVersion");
const roonProduct = b.read((state) => state.roonProduct, "roonProduct");
const settingsIsVisible = b.read(
  (state) => state.settingsIsVisible,
  "settingsIsVisible"
);
const newMessageIsVisible = b.read(
  (state) => state.newMessageIsVisible,
  "newMessageIsVisible"
);
const isLoading = b.read((state) => state.isLoading, "isLoading");

export const getters = {
  get userId() {
    return userId();
  },
  get language() {
    return language();
  },
  get messages() {
    return messages();
  },
  get isLoading() {
    return isLoading();
  },
  get settingsIsVisible() {
    return settingsIsVisible();
  },
  get newMessageIsVisible() {
    return newMessageIsVisible();
  },
  get deviceId() {
    return deviceId();
  },
  get fcmToken() {
    return fcmToken();
  },
  get roonPlatform() {
    return roonPlatform();
  },
  get roonVersion() {
    return roonVersion();
  },
  get roonProduct() {
    return roonProduct();
  },
};

// mutations
function persistStateInLocalStorage(state: InboxState) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
}
function setUserId(state: InboxState, userId: string) {
  state.userId = userId;
  persistStateInLocalStorage(state);
}
function setDeviceId(state: InboxState, deviceId: string) {
  state.deviceId = deviceId;
  persistStateInLocalStorage(state);
}
function setFcmToken(state: InboxState, fcmToken: string) {
  state.fcmToken = fcmToken;
}
function setRoonPlatform(state: InboxState, roonPlatform: string) {
  state.roonPlatform = roonPlatform;
}
function setRoonVersion(state: InboxState, roonVersion: string) {
  state.roonVersion = roonVersion;
}
function setRoonProduct(state: InboxState, roonProduct: string) {
  state.roonProduct = roonProduct;
}
function setLanguage(state: InboxState, language: string) {
  state.language = language;
  persistStateInLocalStorage(state);
}
function setMessages(state: InboxState, messages: Message[]) {
  state.messages = messages;
}
function removeMessage(state: InboxState, message: Message) {
  state.messages = state.messages.filter((m) => m.id !== message.id);
}
function showSettings(state: InboxState) {
  state.settingsIsVisible = true;
}
function hideSettings(state: InboxState) {
  state.settingsIsVisible = false;
}
function showNewMessage(state: InboxState) {
  state.newMessageIsVisible = true;
}
function hideNewMessage(state: InboxState) {
  state.newMessageIsVisible = false;
}
function startLoading(state: InboxState) {
  state.isLoading = true;
}
function endLoading(state: InboxState) {
  state.isLoading = false;
}

export const mutations = {
  setUserId: b.commit(setUserId),
  setLanguage: b.commit(setLanguage),
  setMessages: b.commit(setMessages),
  removeMessage: b.commit(removeMessage),
  setDeviceId: b.commit(setDeviceId),
  setFcmToken: b.commit(setFcmToken),
  setRoonPlatform: b.commit(setRoonPlatform),
  setRoonProduct: b.commit(setRoonProduct),
  setRoonVersion: b.commit(setRoonVersion),
  showSettings: b.commit(showSettings),
  hideSettings: b.commit(hideSettings),
  showNewMessage: b.commit(showNewMessage),
  hideNewMessage: b.commit(hideNewMessage),
  startLoading: b.commit(startLoading),
  endLoading: b.commit(endLoading),
};

// actions
type ActionContext = BareActionContext<InboxState, RootState>;

async function loadMessages(context: ActionContext) {
  const {
    state: { userId },
  } = context;
  if (!userId) {
    return;
  }

  try {
    mutations.startLoading();
    const messages = await Messages.loadMessages(userId);
    mutations.setMessages(messages);
  } finally {
    mutations.endLoading();
  }
}

function loadStateFromLocalStorage() {
  const serializedState = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!serializedState) {
    return;
  }

  const {
    userId,
    language,
    deviceId,
    roonPlatform,
    roonVersion,
    roonProduct,
  } = JSON.parse(serializedState);
  if (userId) {
    mutations.setUserId(userId);
  }
  if (language) {
    mutations.setLanguage(language);
  }
  if (deviceId) {
    mutations.setDeviceId(deviceId);
  }
  if (roonPlatform) {
    mutations.setRoonPlatform(roonPlatform);
  }
  if (roonProduct) {
    mutations.setRoonProduct(roonProduct);
  }
  if (roonVersion) {
    mutations.setRoonVersion(roonVersion);
  }
}

async function revokeMessage(context: ActionContext, message: Message) {
  mutations.removeMessage(message);
  await Messages.revokeMessage(message.userId, message.id!);
}

export const actions = {
  loadMessages: b.dispatch(loadMessages),
  loadStateFromLocalStorage: b.dispatch(loadStateFromLocalStorage),
  revokeMessage: b.dispatch(revokeMessage),
};
